import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, MessagesSquare, Plus, Search } from 'lucide-react';
import { fetchConversations, type ConversationRow } from '../../api/conversations';
import { threadTopic } from '@peles/shared';
import { useCan } from '../../hooks/useCan';
import { ConversationView } from './ConversationView';
import { NewConversationModal } from './NewConversationModal';
import { chipTone, relativeTime } from './conversationFormat';

const PAGE_SIZE = 25;
const THREAD_PARAM = 'thread';

export interface RecordConversationListProps {
  /** `THREAD_ENTITY_TYPES`' spelling — `Event`, `Vendor`, … */
  entityType: string;
  entityId: string;
  /** Names the record in the empty state ("about this event"). */
  recordNoun?: string;
}

/** Who a conversation is between, when it has no subject of its own. */
function participantNames(row: ConversationRow): string {
  const names = row.participants.map((p) => p.name).filter(Boolean) as string[];
  if (names.length === 0) return 'Conversation';
  return names.length > 3
    ? `${names.slice(0, 3).join(', ')} +${names.length - 3}`
    : names.join(', ');
}

function initials(name: string | null): string {
  const parts = (name ?? '?').trim().split(/\s+/);
  return (
    (parts[0]?.[0] ?? '?') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')
  ).toUpperCase();
}

/**
 * One record's conversations, as a list beside the one that is open — the
 * event workspace's Conversations tab, and any record page that wants the same.
 *
 * ⚠️ **The list is the screen, not a button onto it.** The tab used to render
 * the package's `InquiryButton`: a count ("Inquiries 19") that had to be
 * clicked before anything was shown, and named every conversation an inquiry.
 * The rows here are the caller's own conversations about the record
 * (`GET /threads/conversations?entityType&entityId` — the server scopes it to
 * the caller, as it always does), newest activity first, each with who is on
 * it, the last message and what is unread.
 *
 * ⚠️ **An event's list includes its records' conversations.** A thread
 * started from an expense or a task is about the event too, so the server
 * answers those for `Event` as well; each such row names the record it is
 * about ("Expense: Catering") so it is not mistaken for one about the event.
 *
 * ⚠️ **The open conversation lives in the address** (`?thread=`), so a link
 * from a notification, a reload or the browser's back button lands on it. On a
 * phone the list gives way to the conversation, with a way back.
 */
export function RecordConversationList({
  entityType,
  entityId,
  recordNoun = 'this record',
}: RecordConversationListProps) {
  const [params, setParams] = useSearchParams();
  const openId = params.get(THREAD_PARAM);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [newOpen, setNewOpen] = useState(false);
  const canStart = useCan('conversations.start');
  const queryClient = useQueryClient();

  // Searching waits for a pause in typing, and starts again from the top.
  useEffect(() => {
    const handle = setTimeout(() => {
      setQuery(search.trim());
      setLimit(PAGE_SIZE);
    }, 250);
    return () => clearTimeout(handle);
  }, [search]);

  const queryKey = ['conversations', 'record', entityType, entityId, query, limit] as const;
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchConversations({
        entityType,
        entityId,
        limit,
        ...(query ? { search: query } : {}),
      }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  const open = (threadId: string | null) => {
    const next = new URLSearchParams(params);
    if (threadId) next.set(THREAD_PARAM, threadId);
    else next.delete(THREAD_PARAM);
    setParams(next, { replace: !threadId });
  };

  // Reading a conversation clears its unread; the list should say so.
  const refresh = () =>
    void queryClient.invalidateQueries({
      queryKey: ['conversations', 'record', entityType, entityId],
    });

  // Leaving a conversation by any road — the back button, "All conversations",
  // another row — refreshes the list, so its unread badge is not left behind.
  const lastOpen = useRef(openId);
  useEffect(() => {
    if (lastOpen.current && lastOpen.current !== openId) refresh();
    lastOpen.current = openId;
  }, [openId]);

  // The record is handed to the conversation only when this list shows it is
  // about that record; a thread named in the address from elsewhere reads its
  // own record from the server, so its people picker offers the right people.
  const openRow = rows.find((row) => row.id === openId);
  const openRecord =
    openRow && openRow.entityType === entityType && openRow.entityId === entityId
      ? { entityType, entityId }
      : null;

  const newButton = canStart ? (
    <button
      type="button"
      onClick={() => setNewOpen(true)}
      className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
      data-testid="record-new-conversation"
    >
      <Plus size={16} />
      New conversation
    </button>
  ) : null;

  const empty = !isLoading && !isError && rows.length === 0;

  return (
    <div
      className="flex min-h-[32rem] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white lg:flex-row"
      data-testid="record-conversation-list"
    >
      {/* The list — hidden on a phone while a conversation is open. */}
      <div
        className={`flex flex-col border-gray-200 lg:w-96 lg:shrink-0 lg:border-r ${
          openId ? 'hidden lg:flex' : 'flex'
        }`}
      >
        <div className="flex items-center gap-2 border-b border-gray-100 p-3">
          <label className="relative flex-1">
            <span className="sr-only">Search conversations</span>
            <Search
              size={15}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search conversations"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
          {canStart && (
            <button
              type="button"
              onClick={() => setNewOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg bg-primary-600 p-2 text-sm font-medium text-white hover:bg-primary-700"
              aria-label="New conversation"
              title="New conversation"
            >
              <Plus size={16} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto" aria-busy={isFetching}>
          {isLoading && (
            <ul aria-label="Loading conversations">
              {Array.from({ length: 5 }, (_, i) => (
                <li key={i} className="flex gap-3 border-b border-gray-50 px-4 py-3">
                  <div className="h-9 w-9 animate-pulse rounded-full bg-gray-100" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                    <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
                  </div>
                </li>
              ))}
            </ul>
          )}

          {isError && (
            <div className="p-6 text-center text-sm text-gray-600">
              <p>The conversations could not be loaded.</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-2 text-primary-600 underline"
              >
                Try again
              </button>
            </div>
          )}

          {empty && (
            <div className="px-6 py-12 text-center" data-testid="record-conversations-empty">
              <MessagesSquare className="mx-auto mb-3 text-gray-300" size={36} />
              <p className="text-sm font-medium text-gray-800">
                {query
                  ? 'No conversation matches that search.'
                  : `No conversations about ${recordNoun} yet.`}
              </p>
              {!query && (
                <p className="mt-1 text-xs text-gray-500">
                  Start one with the client, a vendor or the office — replies arrive here.
                </p>
              )}
            </div>
          )}

          {rows.length > 0 && (
            <ul>
              {rows.map((row) => {
                const unread = row.unreadCount ?? 0;
                const active = row.id === openId;
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => open(row.id)}
                      aria-current={active ? 'true' : undefined}
                      className={`flex w-full gap-3 border-b border-gray-50 px-4 py-3 text-left transition-colors ${
                        active ? 'bg-primary-50' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`record-conversation-${row.id}`}
                    >
                      <span className="flex -space-x-2 pt-0.5">
                        {row.participants.slice(0, 2).map((p) => (
                          <span
                            key={p.recipientId}
                            className={`inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-[11px] font-semibold ${chipTone(p.recipientId)}`}
                            title={p.name ?? undefined}
                          >
                            {initials(p.name)}
                          </span>
                        ))}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span
                            className={`truncate text-sm ${unread > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}
                          >
                            {row.subject || participantNames(row)}
                          </span>
                          <span className="shrink-0 text-[11px] text-gray-400">
                            {relativeTime(row.updatedAt)}
                          </span>
                        </span>
                        {row.subject && (
                          <span className="block truncate text-xs text-gray-500">
                            {participantNames(row)}
                          </span>
                        )}
                        {row.entityType &&
                          (row.entityType !== entityType || row.entityId !== entityId) && (
                            <span
                              className="mt-0.5 block truncate text-[11px] font-medium text-primary-700"
                              data-testid={`record-conversation-topic-${row.id}`}
                            >
                              {threadTopic(row.entityType, row.entityId, row.entityName)}
                            </span>
                          )}
                        <span className="mt-0.5 flex items-center gap-2">
                          <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
                            {row.preview
                              ? `${row.previewSender ? `${row.previewSender}: ` : ''}${row.preview}`
                              : 'No messages yet'}
                          </span>
                          {row.status !== 'open' && (
                            <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] capitalize text-gray-500">
                              {row.status}
                            </span>
                          )}
                          {unread > 0 && (
                            <span
                              className="shrink-0 rounded-full bg-primary-600 px-1.5 text-[10px] font-semibold leading-4 text-white"
                              aria-label={`${unread} unread`}
                            >
                              {unread}
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {rows.length < total && (
            <div className="p-3 text-center">
              <button
                type="button"
                onClick={() => setLimit((n) => n + PAGE_SIZE)}
                disabled={isFetching}
                className="text-sm text-primary-600 hover:underline disabled:opacity-50"
              >
                {isFetching ? 'Loading…' : `Show more (${total - rows.length})`}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* The open conversation, or a prompt to pick one. */}
      <div className={`min-w-0 flex-1 ${openId ? 'flex' : 'hidden lg:flex'} flex-col`}>
        {openId ? (
          <div className="flex-1 p-4">
            <ConversationView
              key={openId}
              threadId={openId}
              record={openRecord}
              requireRecord
              header={
                <button
                  type="button"
                  onClick={() => open(null)}
                  className="mb-3 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 lg:hidden"
                >
                  <ArrowLeft size={15} />
                  All conversations
                </button>
              }
              onClose={() => open(null)}
            />
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
            <MessagesSquare className="text-gray-300" size={44} />
            <p className="text-sm text-gray-600">
              {rows.length > 0
                ? 'Choose a conversation to read it here.'
                : `Nothing about ${recordNoun} yet.`}
            </p>
            {newButton}
          </div>
        )}
      </div>

      <NewConversationModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        scope={{ entityType, entityId }}
        onCreated={(threadId) => {
          setNewOpen(false);
          refresh();
          open(threadId);
        }}
      />
    </div>
  );
}
